import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save, Mail, Clock, CheckCircle2, AlertTriangle, X, Send } from "lucide-react";

export default function AdminEmails() {
  const [tab, setTab] = useState("sequence");
  const [templates, setTemplates] = useState([]);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/admin/email-templates"), api.get("/admin/email-log")])
      .then(([t, l]) => { setTemplates(t.data); setLog(l.data); })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const statusMeta = {
    sent: [CheckCircle2, "text-primary", "Sent"],
    scheduled: [Clock, "text-muted-foreground", "Scheduled"],
    skipped: [AlertTriangle, "text-accent", "Skipped"],
    failed: [AlertTriangle, "text-accent", "Failed"],
  };

  const notConfigured = log.some((e) => e.status === "skipped");

  return (
    <div>
      <h1 className="font-heading font-black text-3xl tracking-tight">Email Funnel</h1>
      <p className="text-muted-foreground mt-2">
        Every lead-magnet signup gets a welcome email + a 3-step nurture sequence toward the $99 Toolkit.
      </p>

      {notConfigured && (
        <div className="mt-6 bg-amber-50 border border-amber-300 text-amber-900 px-5 py-4 text-sm flex items-start gap-3" data-testid="emails-sendgrid-warning">
          <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5 text-amber-600" />
          <div>
            <strong>Email provider not connected yet.</strong> Emails are being queued and logged but not sent.
            Add your Resend API key to go live — every "Skipped" email will then send automatically.
          </div>
        </div>
      )}

      <div className="flex gap-2 mt-8 border-b border-border">
        {[["sequence", "Sequence"], ["activity", "Activity log"]].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`emails-tab-${k}`}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px ${tab === k ? "border-primary text-primary" : "border-transparent text-muted-foreground"}`}>
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary" /></div>
      ) : tab === "sequence" ? (
        <div className="mt-8">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-4">Lead nurture sequence</div>
          <div className="space-y-4">
            {templates.filter((t) => t.type !== "transactional").map((t, i) => (
              <div key={t.key} data-testid={`email-template-${t.key}`} className="bg-white border border-border p-6 flex items-center gap-5">
                <div className="w-11 h-11 bg-secondary flex items-center justify-center shrink-0">
                  {i === 0 ? <Send className="w-5 h-5 text-primary" /> : <Mail className="w-5 h-5 text-primary" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] uppercase tracking-wider font-semibold text-primary">
                    {t.delay_days === 0 ? "Immediately on signup" : `Day ${t.delay_days}`}
                  </div>
                  <div className="font-semibold truncate">{t.subject}</div>
                </div>
                <button onClick={() => setEditing(t)} data-testid={`email-edit-${t.key}`}
                  className="text-sm font-semibold text-primary border border-border px-4 py-2 hover:border-primary">
                  Edit
                </button>
              </div>
            ))}
          </div>

          {templates.some((t) => t.type === "transactional") && (
            <>
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold mt-10 mb-4">Transactional emails</div>
              <div className="space-y-4">
                {templates.filter((t) => t.type === "transactional").map((t) => (
                  <div key={t.key} data-testid={`email-template-${t.key}`} className="bg-white border border-border p-6 flex items-center gap-5">
                    <div className="w-11 h-11 bg-primary/10 flex items-center justify-center shrink-0">
                      <CheckCircle2 className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] uppercase tracking-wider font-semibold text-primary">Instantly on purchase</div>
                      <div className="font-semibold truncate">{t.subject}</div>
                    </div>
                    <button onClick={() => setEditing(t)} data-testid={`email-edit-${t.key}`}
                      className="text-sm font-semibold text-primary border border-border px-4 py-2 hover:border-primary">
                      Edit
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="bg-white border border-border mt-8 overflow-x-auto">
          {log.length === 0 ? (
            <div className="p-10 text-center text-muted-foreground">No emails yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  <th className="p-4 font-medium">Recipient</th>
                  <th className="p-4 font-medium">Email</th>
                  <th className="p-4 font-medium">Status</th>
                  <th className="p-4 font-medium">Scheduled</th>
                </tr>
              </thead>
              <tbody>
                {log.map((e) => {
                  const [Icon, color, label] = statusMeta[e.status] || statusMeta.scheduled;
                  return (
                    <tr key={e.id} data-testid={`email-log-${e.id}`} className="border-b border-border last:border-0">
                      <td className="p-4">{e.email}</td>
                      <td className="p-4 text-muted-foreground">{e.subject}</td>
                      <td className="p-4"><span className={`inline-flex items-center gap-1.5 ${color}`}><Icon className="w-4 h-4" /> {label}</span></td>
                      <td className="p-4 text-muted-foreground">{new Date(e.scheduled_at).toLocaleString()}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {editing && <EditModal template={editing} onClose={() => setEditing(null)} onDone={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function EditModal({ template, onClose, onDone }) {
  const [form, setForm] = useState({ subject: template.subject, body_html: template.body_html, delay_days: template.delay_days });
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put(`/admin/email-templates/${template.key}`, {
        subject: form.subject,
        body_html: form.body_html,
        delay_days: parseInt(form.delay_days),
      });
      toast.success("Email updated.");
      onDone();
    } catch {
      toast.error("Update failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="email-edit-modal">
      <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <h2 className="font-heading font-bold text-xl">Edit email · {template.key}</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={save} className="p-5 space-y-4">
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2">
              <label className="text-sm font-medium block mb-2">Subject</label>
              <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="email-edit-subject"
                className="w-full border border-border px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary" />
            </div>
            <div>
              <label className="text-sm font-medium block mb-2">Send after (days)</label>
              <input type="number" min="0" value={form.delay_days} onChange={(e) => setForm({ ...form, delay_days: e.target.value })} data-testid="email-edit-delay"
                className="w-full border border-border px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary" />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium block mb-2">Body (HTML — wrapped in branded layout automatically)</label>
            <textarea rows={10} value={form.body_html} onChange={(e) => setForm({ ...form, body_html: e.target.value })} data-testid="email-edit-body"
              className="w-full border border-border px-4 py-2.5 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-primary" />
            <p className="text-xs text-muted-foreground mt-2">
              Tokens: <code>[[TOOLKIT_URL]]</code>, <code>[[PRODUCTS_URL]]</code>, <code>[[PORTAL_URL]]</code>, <code>[[PRODUCT]]</code>, <code>[[AMOUNT]]</code>. Changes apply to future emails.
            </p>
          </div>
          <button type="submit" disabled={busy} data-testid="email-edit-save"
            className="w-full bg-primary text-white py-3 font-semibold btn-lift disabled:opacity-60 inline-flex items-center justify-center gap-2">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Save className="w-4 h-4" /> Save email</>}
          </button>
        </form>
      </div>
    </div>
  );
}
